/**
 * The panel's API client — every `/app/*` request the control panel makes goes through here
 * (ADR 0021 decisions 3, 4 and 6).
 *
 * **Its own client, deliberately.** Decision 6 defers a shared contract package until mobile
 * is a real second caller; until then this is a fetch wrapper with the two rules that make it
 * correct, and no generated types nobody has a second consumer for.
 *
 * **The credential is read per request and never held.** This is the single most important
 * line in the file and the one the ADR calls out by name: *"The client must call
 * `getTokens()` per request and never cache the result. A five-minute JWT held across a
 * sixty-minute session goes stale twelve times over, and the failure is a control panel that
 * works for five minutes after sign-in."* So {@link appFetch} takes a **function** rather than
 * a token, and calls it inside the request it is about to make. A caller that reads the token
 * first and passes the string cannot compile.
 *
 * **`credentials: "omit"`, stated rather than inherited.** `fetch` already withholds cookies
 * cross-origin by default, so this changes no behaviour — it makes the rule visible on the
 * only line that could ever break it. ADR 0021 decision 4's invariant is *explicit, never
 * ambient*: the Stytch cookie is on `.iamboone.app` and therefore rides every request to
 * `mcp[-dev].iamboone.app` unless something stops it, and the server refuses to read one
 * (`tests/invariants/credentials-are-never-ambient.test.ts`). This is that rule's near half.
 *
 * **The header, not the body.** `credential.ts` accepts either, and reads have no body to put
 * it in; using one form for every request means there is one thing to look for in a network
 * panel and no rule about which method carries what.
 */

/**
 * A request that did not produce data — a refusal, a 404, or a network the browser could not
 * reach.
 *
 * `code` is the server's own `error` string (`no_family`, `family_deleting`,
 * `session_required`, …), which is what a caller branches on. It is deliberately the same
 * vocabulary an MCP host sees, so a refusal means one thing in the product rather than one
 * thing per surface.
 */
export class AppRequestError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * The server's own family-facing sentence, when the refusal carried one.
   *
   * **A refusal from a write route is usually an answer rather than a fault** — a spent invite
   * allowance, an owners-only tool, a family being erased — and `#298`'s `refusal()` relays the
   * tool's own `message` for that reason: several of them name the only way out of a state the
   * product otherwise cannot leave. Dropping it here and rendering a generic line instead would
   * put the panel in the position ADR 0021 decision 5 refuses, of inventing its own account of
   * an enforcement decision it did not make.
   *
   * `null` for a refusal that carried no message, and for anything that was not one — a 502
   * from in front of the server is HTML, and `errorCode` already declines to trust it. It is
   * deliberately **not** `Error.message`, which stays the developer-facing string: one of them
   * is for a console and the other is for a family, and a single field would be read as both.
   */
  readonly detail: string | null;

  constructor(status: number, code: string, detail: string | null = null) {
    super(`app request failed: ${status} ${code}`);
    this.name = "AppRequestError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/**
 * The code for a request that never reached the server — offline, DNS, a blocked origin.
 *
 * A status of 0 rather than a made-up 5xx: nothing answered, and claiming the server did is
 * how a transient network problem gets reported to somebody as a fault in their family's data.
 */
export const UNREACHABLE = "unreachable";

/** The header `services/mcp/src/app/credential.ts` reads. One name, spelled once here. */
export const SESSION_HEADER = "x-boone-session";

/**
 * Reads the session JWT the browser currently holds, or `null` when there is none.
 *
 * A function rather than a value — see the module header. It is called once per request,
 * immediately before that request is sent.
 */
export type SessionTokenSource = () => string | null;

export interface AppApi {
  readonly origin: string;
  readonly token: SessionTokenSource;
}

/**
 * One `/app/*` request.
 *
 * A missing token is `401 session_required` **without a round trip**: it is the same answer
 * the server would give, and asking it to tell us what we already know would spend a request
 * (and a Stytch rate-limit slot the login endpoints share) to learn nothing.
 */
export async function appFetch<T>(
  api: AppApi,
  path: string,
  init: { method?: "GET" | "POST"; body?: unknown } = {},
): Promise<T> {
  // Read here, inside the request. Never above it, never in a hook's dependency array.
  const sessionJwt = api.token();
  if (sessionJwt === null || sessionJwt.length === 0) {
    throw new AppRequestError(401, "session_required");
  }

  const method = init.method ?? "GET";
  const headers: Record<string, string> = { [SESSION_HEADER]: sessionJwt };
  if (init.body !== undefined) headers["content-type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${api.origin}${path}`, {
      method,
      headers,
      // See the module header: the panel's credential is the header above and nothing else.
      credentials: "omit",
      ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    });
  } catch {
    throw new AppRequestError(0, UNREACHABLE);
  }

  // Read the body before branching: a refusal carries `{ error }` and that string is the
  // whole of what a caller can act on.
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new AppRequestError(response.status, errorCode(payload), refusalMessage(payload));
  }

  return payload as T;
}

/**
 * The `error` string out of a refusal body, or a stand-in.
 *
 * Narrowed rather than trusted: this came off the wire, and a 502 from something in front of
 * the server is HTML. `unknown_error` is what a caller falls back to, and no caller may treat
 * it as a state of its own — it means *the status is all we know*.
 */
function errorCode(payload: unknown): string {
  if (typeof payload === "object" && payload !== null) {
    const { error } = payload as { error?: unknown };
    if (typeof error === "string" && error.length > 0) return error;
  }
  return "unknown_error";
}

/**
 * The server's family-facing sentence out of a refusal body, or `null`.
 *
 * Narrowed the same way {@link errorCode} is and for the same reason: this is untrusted bytes,
 * and a body that is not a refusal envelope has no `message` worth rendering. Only relayed
 * when the payload also names a `code` — a `message` on its own is not something #298's
 * `refusal()` produces, so treating one as family-facing copy would be trusting a shape
 * nothing in the product sends.
 */
function refusalMessage(payload: unknown): string | null {
  if (typeof payload !== "object" || payload === null) return null;
  const { error, message } = payload as { error?: unknown; message?: unknown };
  if (typeof error !== "string" || error.length === 0) return null;
  return typeof message === "string" && message.length > 0 ? message : null;
}
